////////////////////////////////////////
// IMPORTING MODULES
////////////////////////////////////////

import { ArrowLeft } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

////////////////////////////////////////
// COMPONENT: PRIVACY POLICY
////////////////////////////////////////

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  const currentYear = new Date().getFullYear();
  const effectiveDate = "December 14, 2025";

  return (
    <div className="min-h-screen bg-[#0e1220] text-gray-100 overflow-x-hidden">
      <header className="border-b border-gray-700/50 bg-[#0e1220]/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-800 transition-colors text-gray-300 hover:text-gray-100"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-bold text-3xl mb-2 text-gray-100">
            Flight Trail
          </h1>
          <h2 className="text-xl text-gray-300 mb-4">Privacy Policy</h2>
          <p className="text-sm text-gray-500">
            Effective Date: {effectiveDate}
          </p>
        </div>

        {/* Introduction */}
        <section className="mb-10">
          <h2 className="font-bold text-xl mb-4 text-gray-100">
            1. Introduction
          </h2>
          <p className="mb-4 text-sm leading-relaxed text-gray-300">
            This Privacy Policy explains how{" "}
            <strong className="text-gray-100">Neel Bansal</strong> ("Owner,"
            "we," "us," or "our") collects, uses, discloses, and protects your
            information when you use the Flight Trail mobile application (the
            "App").
          </p>
          <p className="text-sm leading-relaxed text-gray-300">
            By using the App, you consent to the collection and use of your
            information as described in this Privacy Policy. If you do not agree
            with this policy, please do not use the App.
          </p>
        </section>

        {/* Information We Collect */}
        <section className="mb-10">
          <h2 className="font-bold text-xl mb-4 text-gray-100">
            2. Information We Collect
          </h2>

          <h3 className="font-semibold text-base mb-2 text-gray-100">
            2.1 Information You Provide
          </h3>
          <p className="mb-4 text-sm leading-relaxed text-gray-300">
            When you create an account or use the App, you may provide us with:
          </p>
          <ul className="list-disc pl-8 mb-4 text-sm leading-relaxed text-gray-300 space-y-2">
            <li>
              <strong className="text-gray-100">Account Information:</strong>{" "}
              Email address and authentication credentials
            </li>
            <li>
              <strong className="text-gray-100">Travel Data:</strong> Airports
              visited, visit dates, and travel history that you choose to log
            </li>
            <li>
              <strong className="text-gray-100">User Preferences:</strong> App
              settings and customization choices (e.g., globe colors, display
              preferences)
            </li>
          </ul>

          <h3 className="font-semibold text-base mb-2 text-gray-100">
            2.2 Information Collected Automatically
          </h3>
          <p className="mb-4 text-sm leading-relaxed text-gray-300">
            When you use the App, we may automatically collect:
          </p>
          <ul className="list-disc pl-8 mb-4 text-sm leading-relaxed text-gray-300 space-y-2">
            <li>
              <strong className="text-gray-100">Device Information:</strong>{" "}
              Device type, operating system version, and unique device
              identifiers
            </li>
            <li>
              <strong className="text-gray-100">Usage Data:</strong> App
              features accessed, interactions, and performance data
            </li>
            <li>
              <strong className="text-gray-100">Log Data:</strong> Error logs
              and crash reports to help us improve the App
            </li>
          </ul>

          <h3 className="font-semibold text-base mb-2 text-gray-100">
            2.3 Information We Do Not Collect
          </h3>
          <p className="text-sm leading-relaxed text-gray-300">
            We do not collect precise location data, contacts, photos, or any
            other sensitive device data. Your travel history is only recorded
            when you manually add airports to the App.
          </p>
        </section>

        {/* How We Use Your Information */}
        <section className="mb-10">
          <h2 className="font-bold text-xl mb-4 text-gray-100">
            3. How We Use Your Information
          </h2>
          <p className="mb-4 text-sm leading-relaxed text-gray-300">
            We use the information we collect to:
          </p>
          <ul className="list-disc pl-8 mb-4 text-sm leading-relaxed text-gray-300 space-y-2">
            <li>Provide, maintain, and improve the App's functionality</li>
            <li>
              Display your travel history and statistics on the interactive
              globe
            </li>
            <li>Sync your data across devices when you sign in</li>
            <li>Respond to your inquiries and support requests</li>
            <li>
              Analyze usage patterns to enhance user experience and fix bugs
            </li>
            <li>Protect against unauthorized access and ensure security</li>
          </ul>
          <p className="text-sm leading-relaxed text-gray-300">
            We do not sell your personal information to third parties. We do not
            use your data for targeted advertising.
          </p>
        </section>

        {/* Data Storage and Security */}
        <section className="mb-10">
          <h2 className="font-bold text-xl mb-4 text-gray-100">
            4. Data Storage and Security
          </h2>
          <p className="mb-4 text-sm leading-relaxed text-gray-300">
            Your data is stored securely using{" "}
            <strong className="text-gray-100">Supabase</strong>, a trusted
            cloud-based backend service. Supabase provides:
          </p>
          <ul className="list-disc pl-8 mb-4 text-sm leading-relaxed text-gray-300 space-y-2">
            <li>Encrypted data transmission (TLS/SSL)</li>
            <li>Encrypted data storage at rest</li>
            <li>Row-level security policies to protect your data</li>
            <li>Regular security audits and compliance certifications</li>
          </ul>
          <p className="text-sm leading-relaxed text-gray-300">
            While we implement industry-standard security measures, no method of
            electronic storage or transmission is 100% secure. We cannot
            guarantee absolute security of your data.
          </p>
        </section>

        {/* Third-Party Services */}
        <section className="mb-10">
          <h2 className="font-bold text-xl mb-4 text-gray-100">
            5. Third-Party Services
          </h2>
          <p className="mb-4 text-sm leading-relaxed text-gray-300">
            The App uses the following third-party services:
          </p>
          <ul className="list-disc pl-8 mb-4 text-sm leading-relaxed text-gray-300 space-y-2">
            <li>
              <strong className="text-gray-100">Supabase:</strong>{" "}
              Authentication and database services
            </li>
            <li>
              <strong className="text-gray-100">Google Play Services:</strong>{" "}
              App distribution and updates
            </li>
            <li>
              <strong className="text-gray-100">OpenFlights:</strong> Airport
              and route data (no user data shared)
            </li>
            <li>
              <strong className="text-gray-100">RestCountries:</strong> Country
              information (no user data shared)
            </li>
          </ul>
          <p className="text-sm leading-relaxed text-gray-300">
            These third-party services have their own privacy policies governing
            how they handle data. We encourage you to review their respective
            policies.
          </p>
        </section>

        {/* Data Sharing */}
        <section className="mb-10">
          <h2 className="font-bold text-xl mb-4 text-gray-100">
            6. Data Sharing
          </h2>
          <p className="mb-4 text-sm leading-relaxed text-gray-300">
            We do not sell, trade, or rent your personal information. We may
            share your information only in the following limited circumstances:
          </p>
          <ul className="list-disc pl-8 mb-4 text-sm leading-relaxed text-gray-300 space-y-2">
            <li>
              <strong className="text-gray-100">With Your Consent:</strong> When
              you explicitly authorize us to share information
            </li>
            <li>
              <strong className="text-gray-100">Service Providers:</strong> With
              trusted third-party services that help us operate the App (e.g.,
              Supabase for data storage), bound by confidentiality obligations
            </li>
            <li>
              <strong className="text-gray-100">Legal Requirements:</strong>{" "}
              When required by law, court order, or governmental authority
            </li>
            <li>
              <strong className="text-gray-100">Protection of Rights:</strong>{" "}
              To protect our rights, privacy, safety, or property, or that of
              our users or the public
            </li>
          </ul>
        </section>

        {/* Your Rights */}
        <section className="mb-10">
          <h2 className="font-bold text-xl mb-4 text-gray-100">
            7. Your Rights and Choices
          </h2>
          <p className="mb-4 text-sm leading-relaxed text-gray-300">
            You have the following rights regarding your personal data:
          </p>
          <ul className="list-disc pl-8 mb-4 text-sm leading-relaxed text-gray-300 space-y-2">
            <li>
              <strong className="text-gray-100">Access:</strong> Request a copy
              of the personal data we hold about you
            </li>
            <li>
              <strong className="text-gray-100">Correction:</strong> Update or
              correct inaccurate information through the App
            </li>
            <li>
              <strong className="text-gray-100">Deletion:</strong> Request
              deletion of your account and all associated data
            </li>
          </ul>

          <div className="bg-[#1a2235] border border-gray-700/50 rounded-lg p-4">
            <h3 className="font-semibold text-base mb-2 text-gray-100">
              Account Deletion
            </h3>
            <p className="text-sm leading-relaxed text-gray-300">
              To delete your account and all associated data, you may use the
              in-app deletion feature or contact us at{" "}
              <strong className="text-cyan-400">
                flighttrailapp@gmail.com
              </strong>
              . We will process your request within 30 days. Upon deletion, all
              your personal data will be permanently removed from our systems,
              except where retention is required for legal compliance.
            </p>
          </div>
        </section>

        {/* Data Retention */}
        <section className="mb-10">
          <h2 className="font-bold text-xl mb-4 text-gray-100">
            8. Data Retention
          </h2>
          <p className="text-sm leading-relaxed text-gray-300">
            We retain your personal data for as long as your account is active
            or as needed to provide you with the App's services. If you delete
            your account, we will delete your personal data within 30 days,
            except for data we are required to retain for legal, accounting, or
            security purposes.
          </p>
        </section>

        {/* Children's Privacy */}
        <section className="mb-10">
          <h2 className="font-bold text-xl mb-4 text-gray-100">
            9. Children's Privacy
          </h2>
          <p className="mb-4 text-sm leading-relaxed text-gray-300">
            The App is suitable for users of all ages. We do not knowingly
            collect personal information from children under 13 without parental
            consent.
          </p>
          <p className="text-sm leading-relaxed text-gray-300">
            If you are a parent or guardian and believe your child has provided
            us with personal information without your consent, please contact us
            at{" "}
            <strong className="text-cyan-400">flighttrailapp@gmail.com</strong>.
            We will take steps to remove such information from our systems.
          </p>
        </section>

        {/* International Data Transfers */}
        <section className="mb-10">
          <h2 className="font-bold text-xl mb-4 text-gray-100">
            10. International Data Transfers
          </h2>
          <p className="text-sm leading-relaxed text-gray-300">
            Your information may be transferred to and processed in countries
            other than your country of residence. These countries may have data
            protection laws different from those in your country. By using the
            App, you consent to the transfer of your information to the United
            States and other jurisdictions where our service providers operate.
          </p>
        </section>

        {/* Changes to Privacy Policy */}
        <section className="mb-10">
          <h2 className="font-bold text-xl mb-4 text-gray-100">
            11. Changes to This Privacy Policy
          </h2>
          <p className="text-sm leading-relaxed text-gray-300">
            We may update this Privacy Policy from time to time. We will notify
            you of any material changes by posting the new Privacy Policy in the
            App and updating the "Effective Date" at the top. Your continued use
            of the App after such changes constitutes your acceptance of the
            updated Privacy Policy. We encourage you to review this policy
            periodically.
          </p>
        </section>

        {/* Contact Information */}
        <section className="mb-10">
          <h2 className="font-bold text-xl mb-4 text-gray-100">
            12. Contact Information
          </h2>
          <p className="mb-4 text-sm leading-relaxed text-gray-300">
            If you have any questions, concerns, or requests regarding this
            Privacy Policy or our data practices, please contact us at:
          </p>
          <div className="bg-[#1a2235] border border-gray-700/50 rounded-lg p-4">
            <p className="text-sm text-gray-100 font-semibold">Neel Bansal</p>
            <p className="text-sm text-gray-300">
              Email:{" "}
              <strong className="text-cyan-400">
                flighttrailapp@gmail.com
              </strong>
            </p>
          </div>
        </section>

        {/* Footer */}
        <footer className="text-center pt-8 pb-8 border-t border-gray-700/50">
          <p className="text-sm text-gray-300 mb-2">
            © {currentYear} Neel Bansal. All rights reserved.
          </p>
          <p className="text-xs text-gray-500">Last Updated: {effectiveDate}</p>
        </footer>
      </div>
    </div>
  );
};

////////////////////////////////////////
// EXPORTING PRIVACY POLICY
////////////////////////////////////////

export default PrivacyPolicy;
